/* The camera on a node: watch it, ask it for a picture, fetch what it saw.
 *
 * Staff only, and for now only here. It works the way the hydrophone does --
 * always recording to the card, live on demand, the receiver pulling what it
 * wants -- and none of it is instant, for the same reason nothing on this page
 * is: there is no command channel. Every button writes something the node
 * picks up when it next checks in, and the card says what it is waiting for
 * rather than spinning.
 */
import { useEffect, useRef, useState } from 'react'
import {
  ApiError, CAMERA_STREAM, api, cancelStill, fmtBytes, fmtTime, pullVideo,
  startLive, stopLive, takeStill,
  type ArchiveJob, type CameraDoc, type CameraInfo, type LiveInfo, type Me,
} from '../api'
import { invalidate, useApi } from '../useApi'
import { useLiveVideo } from '../liveVideo'
import { ArchiveJobs } from './ArchiveJobs'
import { CameraControlsPanel, controlsSettled } from './CameraControls'
import { Rows } from './bits'

type Tab = 'live' | 'stills' | 'recorded' | 'controls'

const errText = (e: unknown, fallback: string) =>
  e instanceof ApiError ? e.detail ?? e.code : fallback

export function NodeCamera({ computeId, missionId, me, camera }: {
  computeId: string
  missionId: string
  me: Me
  camera: CameraInfo
}) {
  const [tab, setTab] = useState<Tab>('live')
  const path = `/v1/computes/${encodeURIComponent(computeId)}/camera`
  // Quick while a still is on its way or the camera has not yet reported
  // the controls it was given, so the answer appears when it lands; slow
  // otherwise, because nothing else here moves on its own.
  const [waiting, setWaiting] = useState(false)
  const q = useApi<CameraDoc>(path, { poll: waiting ? 5000 : 30_000 })
  const anyPending = !!q.data?.stills.some((s) => s.state === 'pending')
    || !controlsSettled(q.data?.controls)
  useEffect(() => { setWaiting(anyPending) }, [anyPending])
  const changed = () => { invalidate(path); q.reload() }

  const TABS: { id: Tab; label: string }[] = [
    { id: 'live', label: 'Live' },
    { id: 'stills', label: 'Stills' },
    { id: 'recorded', label: 'Recorded' },
    { id: 'controls', label: 'Controls' },
  ]

  return (
    <div className="card flush">
      <div className="card-head">
        <h2>Camera</h2>
        <span className={`badge${camera.capturing ? ' on' : ' off'}`}
              title={camera.present === false ? 'Not connected' : undefined}>
          {camera.sensor ?? 'camera'} · {camera.capturing ? 'recording'
            : camera.present === false ? 'not connected' : 'not recording'}
        </span>
        <div className="seg sm" role="tablist" aria-label="Camera">
          {TABS.map((x) => (
            <button key={x.id} type="button" role="tab"
                    aria-selected={tab === x.id}
                    onClick={() => setTab(x.id)}>
              {x.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ padding: '0 14px 14px' }}>
        {/* Kept mounted, hidden, so switching tab does not stop a stream that
            the node is still sending -- and so leaving the page does. */}
        <div hidden={tab !== 'live'}>
          <CameraLive missionId={missionId} canWrite={me.can_write}>
            {/* Beside the picture, so a change can be watched arriving. */}
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 14 }}>Adjust picture</summary>
              <div style={{ paddingTop: 10 }}>
                <CameraControlsPanel computeId={computeId} view={q.data?.controls}
                                     canWrite={me.can_write} onChanged={changed} />
              </div>
            </details>
          </CameraLive>
        </div>
        {tab === 'stills' && (
          q.cold ? <Rows n={3} /> :
          <CameraStills computeId={computeId} doc={q.data}
                        canWrite={me.can_write}
                        onChanged={changed} />
        )}
        {tab === 'recorded' && (
          <CameraRecorded missionId={missionId} doc={q.data}
                          canWrite={me.can_write} />
        )}
        {tab === 'controls' && (
          q.cold ? <Rows n={4} /> : (
            <div className="stack" style={{ gap: 14 }}>
              <CameraControlsPanel computeId={computeId} view={q.data?.controls}
                                   canWrite={me.can_write} onChanged={changed} />
              <CameraSettings doc={q.data} camera={camera} />
            </div>
          )
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------- live */

/** The same sequence as ListenPanel -- ask, wait for the first segment, play --
 *  against the camera's stream. */
function CameraLive({ missionId, canWrite, children }: {
  missionId: string
  canWrite: boolean
  children?: React.ReactNode
}) {
  const video = useLiveVideo(missionId, CAMERA_STREAM)
  const [arming, setArming] = useState(false)
  const [armedAt, setArmedAt] = useState(0)
  const [waited, setWaited] = useState(0)
  const [err, setErr] = useState<string | null>(null)
  // Whether this page started the node streaming, and so owes it a stop.
  const startedHere = useRef(false)

  const watching = arming || video.status !== 'idle'
  const status = useApi<LiveInfo>(
    watching
      ? `/v1/missions/${encodeURIComponent(missionId)}/live?window=45&stream=${CAMERA_STREAM}`
      : null,
    { poll: 3000 },
  )
  const segments = status.data?.segments ?? 0

  const { start: startVideo, status: videoStatus } = video
  useEffect(() => {
    if (arming && segments > 0 && videoStatus === 'idle') {
      setArming(false)
      startVideo()
    }
  }, [arming, segments, videoStatus, startVideo])

  useEffect(() => {
    if (!arming) { setWaited(0); return }
    const t = window.setInterval(
      () => setWaited(Math.floor((Date.now() - armedAt) / 1000)), 500)
    return () => window.clearInterval(t)
  }, [arming, armedAt])

  /* A camera left streaming is ~0.7 GB an hour over the farm's link, and the
   * node's half-hour session limit does not stop it: while desired state says
   * running, the node starts a fresh session. So whoever started it stops it --
   * on Stop, on leaving the page, and on closing the tab, where an ordinary
   * request may be cancelled mid-flight and only a keepalive one survives. */
  useEffect(() => {
    const url = `/v1/missions/${encodeURIComponent(missionId)}/live:stop?stream=${CAMERA_STREAM}`
    const beacon = () => {
      if (!startedHere.current) return
      startedHere.current = false
      void fetch(url, {
        method: 'POST', keepalive: true, credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }, body: '{}',
      }).catch(() => { /* the page is going; nothing to tell */ })
    }
    window.addEventListener('pagehide', beacon)
    return () => {
      window.removeEventListener('pagehide', beacon)
      beacon()
    }
  }, [missionId])

  const watch = async () => {
    if (arming || video.status !== 'idle') return
    setErr(null)
    if (segments > 0) { video.start(); return }
    if (!canWrite) {
      setErr('The camera is not streaming, and your role cannot start it.')
      return
    }
    setArming(true)
    setArmedAt(Date.now())
    try {
      await startLive(missionId, CAMERA_STREAM)
      startedHere.current = true
      void status.reload()
    } catch (e) {
      setArming(false)
      setErr(errText(e, 'Could not start the camera stream.'))
    }
  }

  const stop = async () => {
    video.stop()
    setArming(false)
    if (!canWrite) return
    try {
      await stopLive(missionId, CAMERA_STREAM)
      startedHere.current = false
    } catch {
      /* The picture has stopped; the leave-the-page stop will try again. */
    }
  }

  const playing = video.status === 'playing' || video.status === 'stalled'
  const busy = arming || video.status === 'starting'

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        {!watching ? (
          <button className="btn sm" onClick={() => void watch()}>Watch live</button>
        ) : (
          <button className="btn sm ghost" onClick={() => void stop()}>Stop</button>
        )}
        {playing && (
          <button className="btn sm ghost" onClick={() => video.setMuted(!video.muted)}>
            {video.muted ? 'Unmute' : 'Mute'}
          </button>
        )}
        {playing && (
          <span className="badge alert"><span className="dot-live" /> live</span>
        )}
        {video.latency !== null && (
          <span className="faint num" style={{ fontSize: 13 }}>
            {video.latency.toFixed(1)} s behind
          </span>
        )}
        {busy && (
          <span className="faint" style={{ fontSize: 13 }}>
            {arming
              ? `Waiting for the node to start the camera stream… ${waited} s`
              : 'Starting…'}
          </span>
        )}
      </div>
      {/* Always in the tree: the hook needs the element before it starts. */}
      <video ref={video.ref} muted playsInline
             hidden={!watching}
             style={{ width: '100%', maxHeight: 480, background: '#000',
                      borderRadius: 6 }} />
      {video.status === 'stalled' && (
        <div className="notice">Waiting for the next segment from the node…</div>
      )}
      {(err || video.error) && (
        <div className="notice bad">{err ?? video.error}</div>
      )}
      {children}
      <p className="faint" style={{ fontSize: 12, margin: 0, maxWidth: '74ch' }}>
        The sound is the hydrophone on the left channel and the HydroMoth on the
        right, a few hundred milliseconds apart from the picture — fine for
        watching, not for measuring. Watching stops live sound from this node,
        since a node streams one thing at a time, and it costs about 0.7 GB an
        hour of the farm's uplink, so it stops when you leave this page.
      </p>
    </div>
  )
}

/* ----------------------------------------------------------------- stills */

function CameraStills({ computeId, doc, canWrite, onChanged }: {
  computeId: string
  doc: CameraDoc | undefined
  canWrite: boolean
  onChanged: () => void
}) {
  const [resolution, setResolution] = useState<'video' | 'full'>('video')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const stills = doc?.stills ?? []
  const newest = stills.find((s) => s.state === 'arrived' && s.url)

  const take = async () => {
    setBusy(true)
    setErr(null)
    try {
      await takeStill(computeId, resolution)
      onChanged()
    } catch (e) {
      setErr(errText(e, 'Could not ask for a still.'))
    } finally {
      setBusy(false)
    }
  }

  const cancel = async (rid: string) => {
    try {
      await cancelStill(computeId, rid)
    } catch (e) {
      setErr(errText(e, 'Could not cancel.'))
    } finally {
      onChanged()
    }
  }

  return (
    <div className="stack" style={{ gap: 12 }}>
      {canWrite && (
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <div className="seg sm" role="tablist" aria-label="Resolution">
            <button type="button" role="tab" aria-selected={resolution === 'video'}
                    onClick={() => setResolution('video')}>Quick</button>
            <button type="button" role="tab" aria-selected={resolution === 'full'}
                    onClick={() => setResolution('full')}>Full 4608×2592</button>
          </div>
          <button className="btn sm" disabled={busy} onClick={() => void take()}>
            {busy ? 'Asking…' : 'Take still'}
          </button>
        </div>
      )}
      <p className="faint" style={{ fontSize: 12, margin: 0, maxWidth: '74ch' }}>
        {resolution === 'full'
          ? 'Full resolution stops the video recording for about 13 seconds. '
          : 'Quick is a frame from the running video, at its resolution, with no gap in the recording. '}
        The camera checks for requests every 10 seconds and the picture goes up
        with the node's next upload, so expect about a minute.
      </p>
      {err && <div className="notice bad">{err}</div>}

      {newest?.url && (
        <a href={newest.url} target="_blank" rel="noreferrer">
          <img src={newest.url} alt={`Still taken ${fmtTime(newest.arrived_at)}`}
               style={{ width: '100%', maxHeight: 480, objectFit: 'contain',
                        background: '#000', borderRadius: 6 }} />
        </a>
      )}

      {stills.length > 0 && (
        <table className="tbl">
          <thead>
            <tr><th>Asked</th><th>Kind</th><th>State</th><th className="r">Size</th><th /></tr>
          </thead>
          <tbody>
            {stills.map((s) => (
              <tr key={s.request_id}>
                <td data-l="Asked" className="num">
                  {fmtTime(s.requested_at)}
                  {s.requested_by && <div className="faint" style={{ fontSize: 12 }}>{s.requested_by}</div>}
                </td>
                <td data-l="Kind">{s.resolution === 'full' ? 'Full' : 'Quick'}</td>
                <td data-l="State">
                  <span className={`badge${s.state === 'arrived' ? ' on'
                    : s.state === 'expired' ? ' alert' : ''}`}>{s.state}</span>
                  {s.state === 'expired' && (
                    <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
                      Nothing arrived by {fmtTime(s.not_after)}. The node may
                      have been offline, or the capture failed — see{' '}
                      <span className="mono">journalctl -u phoniq-camera</span>.
                    </div>
                  )}
                </td>
                <td data-l="Size" className="r num">{s.size ? fmtBytes(s.size) : '—'}</td>
                <td>
                  {s.url && (
                    <a className="btn ghost sm" href={s.url} target="_blank" rel="noreferrer">Open</a>
                  )}
                  {canWrite && s.state === 'pending' && (
                    <button className="btn ghost sm"
                            onClick={() => void cancel(s.request_id)}>Cancel</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- recorded */

const PRESETS = [5, 15, 60]

function CameraRecorded({ missionId, doc, canWrite }: {
  missionId: string
  doc: CameraDoc | undefined
  canWrite: boolean
}) {
  const jobsPath = `/v1/missions/${encodeURIComponent(missionId)}/archive-jobs`
  const jobs = useApi<{ items: ArchiveJob[] }>(jobsPath, { poll: 30_000 })
  const video = (jobs.data?.items ?? []).filter((j) => j.format_kind === 'video')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [playing, setPlaying] = useState<string | null>(null)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const pull = async (fromUtc: Date, toUtc: Date) => {
    setBusy(true)
    setErr(null)
    setNote(null)
    try {
      const r = await pullVideo(missionId, fromUtc.toISOString(), toUtc.toISOString())
      setNote(r.horizon.video_note ?? null)
      invalidate(jobsPath)
      jobs.reload()
    } catch (e) {
      setErr(errText(e, 'Could not ask for the video.'))
    } finally {
      setBusy(false)
    }
  }

  const pullRange = () => {
    // datetime-local is the browser's local time, which is what a person
    // typing "14:05" means.
    const a = new Date(from)
    const b = new Date(to)
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b <= a) {
      setErr('Give a start and an end, the end after the start.')
      return
    }
    void pull(a, b)
  }

  const segments = doc?.video ?? []

  return (
    <div className="stack" style={{ gap: 12 }}>
      {canWrite && (
        <>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="faint" style={{ fontSize: 13 }}>Fetch the last</span>
            {PRESETS.map((m) => (
              <button key={m} className="btn sm ghost" disabled={busy}
                      onClick={() => {
                        const now = new Date()
                        void pull(new Date(now.getTime() - m * 60_000), now)
                      }}>
                {m} min
              </button>
            ))}
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)}
                   aria-label="From" />
            <span className="faint">→</span>
            <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)}
                   aria-label="To" />
            <button className="btn sm" disabled={busy || !from || !to}
                    onClick={pullRange}>Fetch</button>
          </div>
        </>
      )}
      <p className="faint" style={{ fontSize: 12, margin: 0, maxWidth: '74ch' }}>
        The node keeps about 12 hours of video and sends it in one-minute
        pieces, roughly 11 MB a minute, at most six hours per request. Pictures
        only: the sound for the same minutes is a separate request on the
        deployment page.
      </p>
      {note && <div className="notice">{note}</div>}
      {err && <div className="notice bad">{err}</div>}

      <ArchiveJobs missionId={missionId} jobs={video} canWrite={canWrite}
                   onChanged={() => { invalidate(jobsPath); jobs.reload() }}
                   note="The node clears video older than its buffer whatever is asked, and clears it before any audio: the hydrophone is the record. A request moves the asked-for minutes to the back of that queue." />

      {playing && (
        <video src={playing} controls autoPlay playsInline
               style={{ width: '100%', maxHeight: 480, background: '#000',
                        borderRadius: 6 }} />
      )}
      {segments.length > 0 && (
        <table className="tbl">
          <thead><tr><th>Minute</th><th>State</th><th className="r">Size</th><th /></tr></thead>
          <tbody>
            {segments.map((v) => (
              <tr key={v.key}>
                <td data-l="Minute" className="num">{fmtTime(v.start_utc)}</td>
                <td data-l="State"><span className={`badge${v.state === 'stored' ? ' on' : ''}`}>{v.state}</span></td>
                <td data-l="Size" className="r num">{v.size ? fmtBytes(v.size) : '—'}</td>
                <td>
                  {v.state === 'stored' && (
                    <SegmentActions objectKey={v.key} onPlay={setPlaying} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** Signed on demand: the URLs live five minutes, and a list of sixty would be
 *  stale before anyone scrolled to the bottom. */
function SegmentActions({ objectKey, onPlay }: {
  objectKey: string
  onPlay: (url: string) => void
}) {
  const [err, setErr] = useState(false)
  const sign = async (download: boolean) => {
    setErr(false)
    try {
      const r = await api.get<{ url: string }>(
        `/v1/objects/url?key=${encodeURIComponent(objectKey)}${download ? '&download=1' : ''}`)
      return r.url
    } catch {
      setErr(true)
      return null
    }
  }
  return (
    <span className="row" style={{ gap: 6 }}>
      <button className="btn ghost sm"
              onClick={() => { void sign(false).then((u) => { if (u) onPlay(u) }) }}>Play</button>
      <button className="btn ghost sm"
              onClick={() => { void sign(true).then((u) => { if (u) window.location.assign(u) }) }}>Download</button>
      {err && <span className="faint" style={{ fontSize: 12 }}>could not sign</span>}
    </span>
  )
}

/* --------------------------------------------------------------- settings */

function CameraSettings({ doc, camera }: { doc: CameraDoc | undefined; camera: CameraInfo }) {
  const eff = doc?.settings.effective ?? camera
  return (
    <div className="stack" style={{ gap: 10 }}>
      <dl className="dl">
        <dt>Picture</dt>
        <dd className="num">
          {eff.width && eff.height ? `${eff.width}×${eff.height}` : '—'}
          {eff.framerate ? ` at ${eff.framerate} fps` : ''}
        </dd>
        <dt>Sensor</dt>
        <dd>{camera.sensor ?? '—'}</dd>
        {doc?.camera?.observed_utc && (
          <>
            <dt>Reported</dt>
            <dd className="num faint">{fmtTime(doc.camera.observed_utc)}</dd>
          </>
        )}
      </dl>
      {doc && !doc.settings.editable && (
        <p className="faint" style={{ fontSize: 12, margin: 0 }}>
          Resolution and frame rate are set in the node's own configuration.
        </p>
      )}
    </div>
  )
}
